import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useOne, useTranslate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { ConfigProvider, theme } from "antd";
// Tab icons now come from the shared buildKnowledgeHubTabs (experience-viewer).
import {
  CaretRightIcon,
  ChartBarIcon,
  DownloadSimpleIcon,
  PencilSimpleIcon,
  ShareNetworkIcon,
} from "@phosphor-icons/react";
import {
  AddOnReadyToasts,
  type AddOnReadyToastItem,
  AlertCard,
  AudioDoc,
  Podcast,
  Breadcrumb,
  CTAButton,
  Divider,
  DocumentPaper,
  DocumentSkeleton,
  DocumentSwitcher,
  ExperienceChatbotPanel,
  ExperiencePageHeader,
  ExperiencePageLayout,
  QuickRecall,
  KnowledgeHubPanel,
  OutOfDatePublicationWidget,
  OutOfDatePublicationCompactButton,
  Quiz,
  ShareModal,
  type ShareSettings,
  SkemaLogo,
  Standouts,
  Summary,
  VideoPosterCard,
  libertifyThemeV2,
  useExpandingOverlay,
  TabBadgeStatus,
} from "@libertify/ui";
import { isSkemaOrganization } from "@libertify.frontend/helpers";
import {
  ChapteredVideoPlayer,
  PdfDocumentViewer,
  getCallToActionFallbackLabelKey,
  getCallToActionIcon,
  getFullLocale,
  getKnowledgeHubTabLabelKey,
  hasChatAddOn,
  resolveCallToActionLabel,
  useCallToActions,
  useAddonAnalytics,
  useAddonVideoAnalytics,
  useExperienceAssets,
  useExperienceChatbot,
  useExperienceDocuments,
  resolveVisibleTabs,
  buildKnowledgeHubTabs,
  useVisitedTabs,
  TAB_ORDER,
  type ExperienceDocument,
  type PdfViewerHandle,
  type MediaProjectInstanceResponse,
  type TabKey,
} from "@libertify/experience-viewer";
import { resolveCallToActions } from "@libertify.frontend/adapters";

import { usePublishUnpublish } from "../media/projects/hooks/usePublishUnpublish";
import { getEditNavState, getEditPagePath } from "./editNavigation";
import { AddOnEditHeader } from "./components/AddOnEditHeader";
import { useOutOfDateDismissal } from "../media/projects/hooks/useOutOfDateDismissal";
import { LabeledToggle } from "../playbooks/components/LabeledToggle";
import { QuotaUpgradeModal } from "components/QuotaUpgradeModal";
import useIsAdmin from "hooks/useIsAdmin";
import { useSession } from "hooks/useSession";
import { useAuth } from "hooks/useAuth";
import { API_URL, CHATBOT_WS_URL } from "../../constants";
import { getPublicationUrl } from "../media/utils";
import { useScormExportOrgIds } from "../media/projects/hooks/useScormExportOrgIds";
import { ScormExportModal } from "../media/projects/components/ScormExportModal";
import { useProjectInstance } from "../media/projects/hooks/useProjectInstance";
import { useRegenerateOrApprove } from "../media/projects/hooks/useRegenerateOrApprove";
import { useKnowledgeHubAddOns } from "./hooks/useKnowledgeHubAddOns";
import { useRegenerateOutOfDateItems } from "./hooks/useRegenerateOutOfDateItems";
import { useWsSubscription } from "hooks/useWsSubscription";
import { isLtiExperience } from "utils/isLtiExperience";
import { getShareAccess } from "utils/getShareAccess";
import type { WSEvent } from "store/websocketStore";
import { is404 } from "utils/network";
import { DEFAULT_INSTANCE_ID } from "./constants";

// Experiences list, the target of the header breadcrumb's first item.
const EXPERIENCES_LIST_PATH = "/media/projects";
// App home, the target of the header logo.
const HOME_PATH = "/home";

/**
 * A crash inside one panel is contained by the layout's SlotErrorBoundary,
 * so it never reaches the page-level Sentry boundary in ExperienceV2Layout.
 * Report it here or a broken panel goes unnoticed.
 */
const reportSlotError = (error: Error, errorInfo: ErrorInfo) => {
  // Without the component stack every slot reports identically and you
  // can't tell which panel failed.
  Sentry.captureException(error, {
    contexts: { react: { componentStack: errorInfo.componentStack } },
  });
};

/**
 * Knowledge Hub experience page for admin.
 *
 * Renders the same `<ExperiencePageLayout>` consumed by the sales app
 * (PR D) with admin's data + routing (Refine + React Router v6).
 *
 * Wrapped in a nested `<ConfigProvider theme={libertifyThemeV2}>` so
 * AntD primitives + theme-aware libs/ui components pick up the
 * theme-v2 tokens for this page only; the rest of admin keeps the
 * legacy theme.
 *
 * Pilot scope: mock data (document title, add-on tabs, chatbot echo).
 * Backend wiring lands in a later PR per the Phase 2 plan.
 */

export const ExperiencePage = () => {
  const { projectId, instanceId } = useParams<{
    projectId: string;
    instanceId?: string;
  }>();
  const t = useTranslate();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const video = useExpandingOverlay();
  const [hubOpen, setHubOpen] = useState(true);
  const [chatbotOpen, setChatbotOpen] = useState(false);

  // SCORM export gate — admin or whitelisted org, same as the publish flow.
  const isAdmin = useIsAdmin();
  const scormExportOrgIds = useScormExportOrgIds();
  const { organizations } = useSession();
  const { accessToken } = useAuth();
  const [scormModalOpen, setScormModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Covers the whole Save; the hook's `isMutating` drops between requests.
  const [isSavingShare, setIsSavingShare] = useState(false);

  // Instance is optional in the URL. When omitted, fall back to the default
  // instance — same convention as CreatorPreview (`instance_id ?? "default"`).
  const requestedInstanceId = instanceId ?? DEFAULT_INSTANCE_ID;

  // Fetch the publication via Refine — same endpoint admin's existing
  // CreatorPreview hits. `enabled` guard prevents a 404 if the URL
  // arrives before params hydrate.
  const requestedInstance = useOne<MediaProjectInstanceResponse>({
    resource: `media/projects/${projectId}/instances`,
    id: requestedInstanceId,
    queryOptions: {
      enabled: Boolean(projectId),
    },
    errorNotification: false,
  });

  // The URL can name an instance the project doesn't have: because :instanceId
  // is optional, a hand-typed `/experience-v2/edit` lands here with instance
  // "edit". When that read 404s, read the default instance instead and work
  // with it, so everything built from the id (Edit link, Share, publish toggle)
  // targets a real one. 404 only — videoless projects have no instance at all
  // and their read 400s. This read stays off
  // until the requested one has failed, and its switch depends only on that
  // first answer, so it can't flip back and forth.
  const requestedInstanceMissing = is404(requestedInstance.error);
  const defaultInstance = useOne<MediaProjectInstanceResponse>({
    resource: `media/projects/${projectId}/instances`,
    id: DEFAULT_INSTANCE_ID,
    queryOptions: {
      enabled: Boolean(projectId) && requestedInstanceMissing,
    },
    errorNotification: false,
  });
  const effectiveInstanceId = requestedInstanceMissing
    ? DEFAULT_INSTANCE_ID
    : requestedInstanceId;
  const {
    data: publicationData,
    isLoading: isPublicationLoading,
    refetch: refetchPublication,
  } = requestedInstanceMissing ? defaultInstance : requestedInstance;
  const publication = publicationData?.data;

  // The real instance id, not the URL's "default" alias — project.media's
  // entries are keyed by their real instance_id.
  const realInstanceId = publication?.instance_id ?? effectiveInstanceId;

  // The project's own media (from `media/projects/{id}`, not the publication
  // snapshot): unlike `publication.media`, this updates live as the video
  // regenerates, without needing a republish. Drives the video tab below.
  const {
    project,
    media,
    instanceSettings,
    isLive,
    isPublicationOutOfDate,
    isMediaOutOfDate,
    isOutOfDateStatusLoading: isProjectInstanceLoading,
    invalidateProject,
    publishedToastId,
    dismissPublishedToast,
    updateSettings,
    updateAccess,
    updateAuthorizedEmails,
    markAsOutOfDate,
  } = useProjectInstance({
    projectId: projectId ?? "",
    instanceId: realInstanceId,
  });
  const isLti = isLtiExperience(media?.publication_mode);
  // Fresh project media, not the publication's possibly-stale cached copy.
  const initialShareAccess = getShareAccess(media?.publication_mode);

  // Keeps `media` fresh while a regeneration is in flight, so this page sees
  // the video flip from Generating to Ready without a manual refetch
  // (MED-6969). Refetches on ANY event for this project rather than only
  // `MediaStatusChanged` — mirrors `useProjectDetails` (the classic detail
  // page, which doesn't show this bug): a regeneration doesn't reliably emit
  // that one specific event, so betting on it alone leaves the tab stuck
  // showing "Generating" if it's dropped. Lives here rather than inside
  // useProjectInstance since this page is the only consumer that needs the
  // video tab to update live.
  const mediaStatusSubscriptionConfig = useMemo(
    () => (projectId ? { type: "project" as const, id: projectId } : []),
    [projectId]
  );
  const handleMediaStatusChange = useCallback(
    (event: WSEvent) => {
      if (event.project_id !== projectId) return;
      invalidateProject().catch((error) => {
        console.error(
          "[ExperiencePage] error refetching project after media status change",
          error
        );
      });
    },
    [projectId, invalidateProject]
  );
  useWsSubscription(mediaStatusSubscriptionConfig, handleMediaStatusChange);

  // Knowledge Hub add-ons: content + realtime generation status + ready
  // toasts, orchestrated in one hook (the video is the project's own media,
  // see above — the publication refetch below only covers the document).
  const {
    quiz,
    summary,
    podcast,
    audioDoc,
    quickRecall,
    standouts,
    addonsLoading,
    projectDocuments,
    projectInfo,
    isTabGenerating,
    isTabErrored,
    readyToasts,
    dismissToast,
    justReadyTabs,
    enabledAddOns,
    enabledAddOnsLoading,
    quizQuestionsPerAttempt,
    podcastAutoPlay,
    podcastSpeedControl,
    sidebarPosition,
    refetchAddOns,
    chatbotStatus,
  } = useKnowledgeHubAddOns({
    projectId: projectId ?? "",
    apiBaseUrl: API_URL,
    token: accessToken,
    videoReady: Boolean(media?.url),
    mediaStatus: media?.status ?? undefined,
    onRefreshPublication: refetchPublication,
  });

  // Org context for the chatbot socket, analytics and the share/SCORM gates:
  // the publication's media org once it exists, else the project's own
  // organization_id — `publication.media` is unavailable while the video
  // generates, and videoless projects never get an instance at all.
  const organizationId =
    publication?.media?.organization ?? projectInfo?.organizationId;

  // The primary document: the publication's Document_0 (the documented
  // convention for its documents map) once the instance has materialized it —
  // else the project's own document, which is user-provided at creation and
  // therefore available immediately (we never wait on the publication for it).
  const mainDocument =
    publication?.documents?.Document_0 ?? projectDocuments[0];

  // Segment analytics for the add-ons: tab opens (e.g. "Quiz Open") + the quiz
  // lifecycle (start / per-answer / attempt) + CTA clicks. Declared here (rather than down by the tabs/quiz code)
  // since the CTA buttons below also need trackCtaClick from this same hook.
  //
  // `workspace_id` rides along per event rather than as a super property:
  // admin registers its super properties once at app startup (withAnalytics in
  // index.tsx), and registerSuperProperties writes to a module-level singleton
  // that is never cleared — so registering it here would leak this page's
  // workspace onto every admin event after navigating away. There is no
  // `tracking_id` counterpart: that identifies an authenticated visitor and is
  // only returned by the public /live endpoint, not the one admin reads.
  const {
    trackAddonTabOpen,
    trackInitialAddonTabOpen,
    trackCtaClick,
    quizAnalyticEvents,
    quickRecallAnalyticEvents,
    summaryAnalyticEvents,
    standoutsAnalyticEvents,
    audioAnalyticEvents,
  } = useAddonAnalytics({
    projectId,
    publicationId: effectiveInstanceId,
    organizationId,
    workspaceId: publication?.workspace_id ?? undefined,
  });

  // media_player_* events for the Video add-on's expanded player — the same
  // family the legacy viewer emits, tagged player_surface: 'addon'.
  const { onPlayerReady } = useAddonVideoAnalytics({
    projectId,
    publicationId: effectiveInstanceId,
    organizationId,
    workspaceId: publication?.workspace_id ?? undefined,
  });

  // Header CTAs: one button per CTA actually configured on the project
  // (whatever the org set up in the CTA editor), using each CTA's own
  // label — falling back to a generic per-type label when none is set
  // for the current locale. Only unresolvable CTAs (no navigable URL)
  // are skipped; every other type still renders with a default icon/label.
  const { i18n } = useTranslation();
  const locale = getFullLocale(i18n.language);
  const callToActions = useCallToActions(publication);
  const ctaButtons = resolveCallToActions(
    callToActions,
    mainDocument?.path
  ).map((cta) => {
    const label = resolveCallToActionLabel(
      cta.labels,
      locale,
      t(getCallToActionFallbackLabelKey(cta.type))
    );
    return {
      id: cta.id,
      icon: getCallToActionIcon(cta.type),
      label,
      onClick: () => {
        trackCtaClick(cta.type, label);
        window.open(cta.url, cta.target);
      },
    };
  });

  // Publish / unpublish toggle — reuses the existing project-instance flow
  // (analytics tracking + quota handling) shared with the classic detail page.
  // The published state is the instance's publication status (MED-6885), read
  // via useProjectInstance's `isLive` — not `media.status` (media generation
  // status never signals "published").
  // Also gates on the publication fetch: `realInstanceId` (and therefore
  // isMediaOutOfDate/isPublicationOutOfDate above) isn't the true instance
  // id until that resolves.
  const isOutOfDateStatusLoading =
    isPublicationLoading || isProjectInstanceLoading;

  const storyboard = project?.storyboards.find(
    (s) => s.id === media?.storyboard_settings_id
  );
  const { regenerateOrApprove } = useRegenerateOrApprove({
    projectId,
    storyboardSettingsId: media?.storyboard_settings_id,
    storyboardStatus: storyboard?.status,
    mediaId: media?.id ?? undefined,
    mediaStatus: media?.status,
    // invalidateProject refreshes isMediaOutOfDate and the video (both read
    // project media); refetchPublication keeps the document/CTAs in sync.
    refetchProjectDetail: () => {
      invalidateProject();
      refetchPublication();
    },
  });
  const { isAddOnsOutOfDate, regenerateOutOfDateItems } =
    useRegenerateOutOfDateItems({
      projectId,
      project,
      isMediaOutOfDate,
      regenerateOrApprove,
      invalidateProject,
    });
  // Media or any add-on is stale — both are fixed by "Regenerate".
  const isItemsOutOfDate = isMediaOutOfDate || isAddOnsOutOfDate;
  const isOutOfDate = isItemsOutOfDate || isPublicationOutOfDate;
  const {
    publishUnpublish,
    republish,
    isMutatingStatus,
    showQuotaModal,
    dismissQuotaModal,
  } = usePublishUnpublish(projectId ?? "", effectiveInstanceId);
  // Once the user picks "Regenerate later"/"Update later", the floating
  // widget stays dismissed across reloads and the compact button takes its
  // place in the header (see MED-6969).
  const {
    isDismissed: isOutOfDateDismissed,
    dismiss: dismissOutOfDateWidget,
    handleRegenerate: handleRegenerateOutOfDate,
    handleUpdate: handleUpdateOutOfDate,
  } = useOutOfDateDismissal({
    projectId: projectId ?? "",
    onRegenerate: regenerateOutOfDateItems,
    // republish resolves to { quotaExceeded, success } (for the toggle
    // flow) — only success is relevant here, to gate clearing the
    // dismissal on an actual successful republish.
    onUpdate: async () => {
      const { success } = await republish();
      // Republishing swaps the publication object — documents, header links,
      // title and palette all render from it — so re-read it, as the
      // regenerate path does. The Knowledge Hub content is read live from
      // the project, but a page opened before the edit still holds the old
      // copy, so re-read that too. The status itself is refetched inside
      // `republish`; without these the page keeps showing what it had until
      // a reload.
      if (success) {
        void refetchPublication();
        refetchAddOns();
      }
      return success;
    },
  });
  const instanceStatus = instanceSettings?.status;
  // Disable until the instance is loaded and its media is in a publishable
  // state (generation not still running).
  const canTogglePublish =
    Boolean(instanceSettings) &&
    instanceStatus !== "Generating" &&
    instanceStatus !== "Pending";
  const handlePublishToggle = useCallback(() => {
    publishUnpublish().catch((error) => {
      console.error("Failed to publish/unpublish:", error);
    });
  }, [publishUnpublish]);
  // Share flow — reuses the project page's publish hooks (same instance as
  // useProjectInstance above, so no need for a second call).
  const handleShareSave = useCallback(
    async (settings: ShareSettings) => {
      let savedAny = false;
      setIsSavingShare(true);
      try {
        try {
          if (settings.access === "password") {
            // Always sent: the saved password can't be read back to compare.
            await updateSettings({
              publication_mode: "PasswordProtected",
              password: settings.password,
            });
            savedAny = true;
          } else if (settings.access !== initialShareAccess) {
            // Skip the PATCH when the mode is unchanged.
            await updateAccess(settings.access === "email");
            savedAny = true;
          }
          // Switching to Email clears the list even with no emails entered.
          if (
            settings.access === "email" &&
            (settings.emails.length > 0 || initialShareAccess !== "email")
          ) {
            await updateAuthorizedEmails(settings.emails);
            savedAny = true;
          }
        } finally {
          // Even a partial save changed the server; the live link needs an Update.
          if (savedAny) {
            await markAsOutOfDate();
          }
        }
        setShareModalOpen(false);
      } finally {
        setIsSavingShare(false);
      }
    },
    [
      updateSettings,
      updateAccess,
      updateAuthorizedEmails,
      markAsOutOfDate,
      initialShareAccess,
    ]
  );

  // NEW-experience SCORM entry point (distinct from the classic publish-flow
  // modal): same admin/whitelisted-org gate, but the package launches the
  // /experience route via getPublicationUrl({ experienceV2: true }).
  const isScormExportEnabled =
    isAdmin || scormExportOrgIds.includes(organizationId ?? "");
  const experienceScormUrl = getPublicationUrl({
    projectId: projectId ?? "",
    publicationId: effectiveInstanceId,
    instanceSlug: publication?.instance_slug ?? undefined,
    organizationSlug: organizations?.find((o) => o.id === organizationId)?.slug,
    experienceV2: true,
  });
  // Pre-fills the modal's "Module title". Prefer the publication title, fall
  // back to the document name. Used as the modal's `key` too, so its
  // `useState(experienceTitle)` re-seeds once the publication resolves (the
  // modal mounts before the fetch completes for admins).
  const scormModuleTitle = publication?.title ?? mainDocument?.name ?? "";

  // Generated video: the progressive mp4 url, from the project's own media
  // (fresh — see `media` above), not the publication's possibly-stale copy.
  const videoUrl = media?.url ?? undefined;
  // The thumbnail only exists on the publication's media (project media has
  // no `metadata`), so it can lag one regeneration behind the video itself.
  const rawThumbnail = publication?.media?.metadata?.["thumbnail"];
  const videoPoster =
    typeof rawThumbnail === "string" ? rawThumbnail : undefined;

  // Highlight asset → chapters (video) + cuepoints + highlights (PDF +
  // scroll sync). Loaded client-side here (admin has no server step);
  // sales does the equivalent server-side in getExperienceData.
  const highlightAssetUrl =
    publication?.document_animation?.highlights?.path ?? undefined;
  const { sections, cuepoints, highlights } =
    useExperienceAssets(highlightAssetUrl);

  const pdfRef = useRef<PdfViewerHandle | null>(null);

  // All documents (not just Document_0), so a chatbot source from any document
  // can be opened. The publication's documents map when the instance has one;
  // else the project's own documents — same shape, and available from creation.
  // Each path is already a usable URL — the same field the viewer renders.
  const sourceDocuments = useMemo(() => {
    const publicationDocuments = Object.values(
      publication?.documents ?? {}
    ).filter((d) => Boolean(d?.path));
    const source =
      publicationDocuments.length > 0 ? publicationDocuments : projectDocuments;
    return source.filter((d) => Boolean(d?.path));
  }, [publication?.documents, projectDocuments]);
  const documents = useMemo<ExperienceDocument[]>(
    () =>
      sourceDocuments.map((d) => ({
        id: d.id,
        url: d.path,
        name: d.name,
        pageCount: d.nb_of_pages,
      })),
    [sourceDocuments]
  );
  const animatedDocumentId =
    publication?.document_animation?.document_id ??
    publication?.documents?.Document_0?.id;
  const documentController = useExperienceDocuments({
    documents,
    animatedDocumentId,
    baseHighlights: highlights,
    baseCuepoints: cuepoints,
    pdfViewerRef: pdfRef,
    sourceHighlightColor: "#14B8A6",
  });
  const documentSummaries = documents.map((d) => ({ id: d.id, name: d.name }));
  // Hidden documents (Extras) get no tab but stay in `documents` for chatbot sources.
  const documentTabs = sourceDocuments
    .filter((d) => !d.hidden)
    .map((d) => ({ id: d.id, name: d.name }));

  // No document yet: show the skeleton while a source is still resolving, the
  // error alert only once everything has settled empty.

  const isDocumentResolving =
    !projectId || isPublicationLoading || addonsLoading;
  const renderDocument = () => {
    if (!mainDocument?.path && isDocumentResolving) {
      return <DocumentSkeleton />;
    }
    if (!mainDocument?.path) {
      return (
        <div style={centeredStyle}>
          <AlertCard
            type="error"
            title={t("experienceV2.document.errorTitle")}
            description={t("experienceV2.document.errorBody")}
          />
        </div>
      );
    }
    return (
      <PdfDocumentViewer
        viewerRef={pdfRef}
        onError={reportSlotError}
        url={documentController.activeDocument?.url ?? mainDocument.path}
        highlights={documentController.highlights}
        cuepoints={documentController.cuepoints}
        documentPagesCount={
          documentController.activeDocument?.pageCount ??
          mainDocument.nb_of_pages
        }
        identifiers={{
          projectId,
          publicationId: effectiveInstanceId,
          organizationId,
        }}
      />
    );
  };

  // The chat session is always built from the project's own documents, never
  // from the published snapshot: it reflects unpublished changes and chat
  // add-on changes made after publishing, and works for videoless projects
  // that have no publication instance (cf MED-4595).
  const chatDocumentIds = useMemo(
    () => projectDocuments.map((doc) => doc.id),
    [projectDocuments]
  );
  const chatOrganizationId = projectInfo?.organizationId;

  // Whether chat is offered at all — drives both the panel slot and the
  // socket. Read from the project's ENABLED ADD-ONS, not the instance's
  // `enable_chat`/`chat_mode`: the add-on config is what the experience was
  // built with, while the instance copy is written separately and is never
  // set for a project created from a playbook.
  //
  // While the read is in flight, stay true so the panel keeps its loading
  // skeleton instead of flashing in and out — an unresolved list is
  // indistinguishable from "no add-ons" here.
  const chatAddOnEnabled = enabledAddOnsLoading || hasChatAddOn(enabledAddOns);
  let chatOffered = chatAddOnEnabled;
  if (!addonsLoading) {
    // A session can only work if there are documents to ground it.
    chatOffered = chatAddOnEnabled && chatDocumentIds.length > 0;
  }

  // The chat stays loading until the Chatbot add-on has generated (MED-7185).
  // OutOfDate is stale but still existing content, so the chat stays usable.
  const chatbotGenerated =
    chatbotStatus === "Generated" || chatbotStatus === "OutOfDate";
  const chatbotErrored = chatbotStatus === "Error";

  // Hold the socket until a complete OpenSocket context exists — connecting
  // earlier sends organization_id: undefined, which the backend never answers.
  // Unlike `chatOffered`, this must not be optimistic while loading —
  // connecting the socket for an experience without chat is a wasted session.
  const chatContextReady =
    chatbotGenerated &&
    !enabledAddOnsLoading &&
    hasChatAddOn(enabledAddOns) &&
    chatDocumentIds.length > 0 &&
    Boolean(chatOrganizationId);

  // Real chatbot over the shared WebSocket hook. Admin auth: the logged-in
  // user's session bearer is used for both bearer_token and password, same as
  // CreatorPreview.
  const resolveAuth = useCallback(
    () => ({
      bearerToken: accessToken ?? undefined,
      password: accessToken ?? undefined,
    }),
    [accessToken]
  );

  const chat = useExperienceChatbot({
    chatbotUrl: CHATBOT_WS_URL,
    projectId: projectId ?? "",
    // The real instance id, not the URL's "default" alias: the REST endpoint
    // resolves the alias but the chatbot's OpenSocket may not. Safe here
    // because the socket only connects once `ready` is true.
    instanceId: publication?.instance_id ?? effectiveInstanceId,
    organizationId: chatOrganizationId,
    // Always document-scoped: built from the project's own documents.
    documentIds: chatDocumentIds,
    resolveAuth,
    assistantAvatar: { name: t("experienceV2.chatbot.assistantInitials") },
    ready: chatContextReady,
    // Hold only while generating (minutes); any other stuck context still times out.
    holdTimeoutUntilReady: !chatbotGenerated,
  });

  const hiddenDocumentIds = useMemo(
    () => new Set(sourceDocuments.filter((d) => d.hidden).map((d) => d.id)),
    [sourceDocuments]
  );

  // On send, collapse the Hub so the chat enlarges
  const chatWithEnlarge = useMemo(
    () => ({
      ...chat,
      // Sources pointing to hidden documents (Extras) are not shown.
      messages: chat.messages.map((m) =>
        m.sources
          ? {
              ...m,
              sources: m.sources.filter(
                (s) => !hiddenDocumentIds.has(s.documentId)
              ),
            }
          : m
      ),
      // A failed generation can't be retried from here, like the other add-ons: no retry button.
      ...(chatbotErrored
        ? { loading: false, failed: true, onRetry: undefined }
        : {}),
      onSubmit: (text: string, source?: "user" | "suggested") => {
        setHubOpen(false);
        chat.onSubmit(text, source);
      },
    }),
    [chat, chatbotErrored, hiddenDocumentIds]
  );

  // Only the add-ons this project actually enabled get a tab — video included,
  // so hiding it in the edit page takes effect here (MED-7123). A `Video` that
  // hasn't finished generating still gets its tab, in a loading state.
  const visibleTabs = resolveVisibleTabs({ availableAddOns: enabledAddOns });

  // Controlled Knowledge Hub tab selection (so a ready-toast can open its tab).
  // `undefined` = the user hasn't picked one yet, in which case the first
  // visible tab is shown. Kept as intent rather than as the rendered value so
  // a selection can't point at a tab this project doesn't have — AntD renders
  // an empty content pane for an `activeKey` matching no item.
  const [selectedTabKey, setSelectedTabKey] = useState<TabKey>();
  const activeTabKey =
    selectedTabKey && visibleTabs.includes(selectedTabKey)
      ? selectedTabKey
      : visibleTabs[0];

  // Video = project media: loading while it's generating or until its url
  // arrives (like v1 — a not-yet-ready video is never a failure); error only on
  // a genuine media failure (MediaStatus Error/Cancelled → isTabErrored).
  const videoLoading =
    isPublicationLoading ||
    isTabGenerating("video") ||
    (!videoUrl && !isTabErrored("video"));

  const isTabLoading = (tab: TabKey): boolean =>
    tab === "video" ? videoLoading : isTabGenerating(tab);

  const { markVisited, isUnread } = useVisitedTabs();
  // The green dot is only a loading→ready transition marker: a tab already
  // ready on load (e.g. right after a page reload) never gets one, even if
  // unvisited — only one that was actually seen loading first (justReadyTabs)
  // qualifies. See useAddOnStatuses' `justReadyTabs` doc for how that's tracked.
  const resolveTabBadgeStatus = (tab: TabKey): TabBadgeStatus | undefined => {
    if (isTabLoading(tab)) return TabBadgeStatus.Loading;
    if (!justReadyTabs.has(tab)) return undefined;
    return isUnread(tab, activeTabKey) ? TabBadgeStatus.Unread : undefined;
  };
  const tabBadgeStatuses: Partial<Record<TabKey, TabBadgeStatus>> =
    Object.fromEntries(
      TAB_ORDER.map((tab) => [tab, resolveTabBadgeStatus(tab)])
    );

  // Each tab carries its own fully-worded message rather than interpolating
  // its label into one template: the labels differ in grammatical number and
  // gender across locales ("Key points" and "Standouts" are plural), so no
  // single "Your {{label}} is ready" sentence stays correct for all of them.
  const readyToastItems: AddOnReadyToastItem[] = readyToasts.map((toast) => ({
    id: toast.id,
    message: t(`experienceV2.addons.toast.ready.${toast.tabKey}`),
  }));
  // readyToasts ids are positive (toastIdRef starts at 1); negating the
  // publish toast's own id keeps the two id spaces from colliding while
  // still giving each publish toast a distinct key.
  const toastItems: AddOnReadyToastItem[] =
    publishedToastId != null
      ? [
          ...readyToastItems,
          {
            id: -publishedToastId,
            message: t("experienceV2.document.publish.toast.published"),
          },
        ]
      : readyToastItems;
  const openAddOnFromToast = (id: number) => {
    if (id < 0) {
      dismissPublishedToast();
      return;
    }
    const toast = readyToasts.find((item) => item.id === id);
    if (!toast) return;
    if (activeTabKey && !isTabLoading(activeTabKey)) markVisited(activeTabKey);
    setSelectedTabKey(toast.tabKey);
    // A toast click is a tab open like any other — without this, the
    // open/close pairing in useAddonAnalytics goes stale and the next manual
    // switch reports a close for a tab that had already lost focus.
    trackAddonTabOpen(toast.tabKey);
    setHubOpen(true);
    dismissToast(id);
  };
  const dismissAnyToast = (id: number) => {
    if (id < 0) {
      dismissPublishedToast();
      return;
    }
    dismissToast(id);
  };

  // AddOnEditHeader's own rendered height under `variant="outlinedAccent"`
  // (2 × paddingSM around a single line of fontSize-m text — see its CSS in
  // libs/ui's Button.tsx) plus its top offset and a matching gap below it.
  // Used to reserve clearance for the two add-ons whose own top-right corner
  // isn't empty (see `reserveTopSpace` below) — everywhere else the button
  // floats over empty space, so nothing needs to move for it.
  const EDIT_HEADER_CLEARANCE =
    token.paddingXS * 2 + token.paddingSM * 2 + token.fontSize;

  // Wraps an add-on's tab content with its own floating "Edit {addon}"
  // button, deep-linking straight to that add-on's card in the Edit page
  // (see editNavigation.ts) — distinct from the page header's own "Edit"
  // CTA, which always opens on General. The wrapper is a growing flex column
  // so the add-on's AddonLayout root still fills the tab (MED-7496).
  //
  // `reserveTopSpace`: Quick recall's "Rated X/Y" counter and Quiz's
  // (vertically centered, so can ride up into the top-right corner on a
  // short container) score screen both put real content where the button
  // floats — pass true to push that add-on's content down below the
  // button instead of letting it render underneath. Every other add-on's
  // top-right corner is empty (a hero poster, a left-aligned title, …), so
  // they keep the plain overlay.
  const withAddOnEditHeader = (
    tab: TabKey,
    content: ReactNode,
    loading: boolean,
    reserveTopSpace = false
  ): ReactNode => (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: "1 0 auto",
      }}
    >
      {loading ? null : (
        <div
          style={{
            position: "absolute",
            top: token.paddingXS,
            right: token.paddingXS,
            zIndex: 1,
          }}
        >
          <AddOnEditHeader
            label={t("experienceV2.document.actions.editAddOn", {
              name: t(getKnowledgeHubTabLabelKey(tab)),
            })}
            onEdit={() =>
              navigate(
                getEditPagePath(projectId, effectiveInstanceId),
                getEditNavState(tab)
              )
            }
          />
        </div>
      )}
      {reserveTopSpace ? (
        <div style={{ height: EDIT_HEADER_CLEARANCE, flexShrink: 0 }} />
      ) : null}
      {content}
    </div>
  );

  // While an add-on is still generating, show its loading state and suppress
  // the error (a not-yet-ready add-on isn't a failure); on a genuine Error
  // status, or the existing content-error, show the error state.
  const tabContent: Record<TabKey, ReactNode> = {
    video: withAddOnEditHeader(
      "video",
      <VideoPosterCard
        poster={videoPoster}
        onPlay={video.expand}
        playLabel={t("experienceV2.video.play")}
        loading={videoLoading}
        error={
          !videoLoading && isTabErrored("video")
            ? t("experienceV2.video.errorTitle")
            : undefined
        }
        errorTitle={t("experienceV2.video.errorTitle")}
      />,
      videoLoading
    ),
    podcast: withAddOnEditHeader(
      "podcast",
      <Podcast
        track={podcast.track}
        autoPlay={podcastAutoPlay}
        speedControl={podcastSpeedControl}
        // On play, collapse the Study Assistant panel so the Knowledge Hub
        // takes the full sidebar width (MED-7075) — layered onto the
        // existing analytics callback rather than a separate prop.
        analyticEvents={{
          ...audioAnalyticEvents,
          onPlay: (info) => {
            audioAnalyticEvents?.onPlay?.(info);
            setChatbotOpen(false);
          },
        }}
        loading={addonsLoading || isTabGenerating("podcast")}
        error={
          !isTabGenerating("podcast") &&
          (podcast.error || isTabErrored("podcast"))
            ? t("experienceV2.audio.error")
            : undefined
        }
      />,
      addonsLoading || isTabGenerating("podcast")
    ),
    audioDoc: (
      <AudioDoc
        track={audioDoc.track}
        analyticEvents={audioAnalyticEvents}
        loading={addonsLoading || isTabGenerating("audioDoc")}
        error={
          !isTabGenerating("audioDoc") &&
          (audioDoc.error || isTabErrored("audioDoc"))
            ? t("experienceV2.audio.error")
            : undefined
        }
      />
    ),
    summary: withAddOnEditHeader(
      "summary",
      <Summary
        content={summary.content || t("experienceV2.summary.empty")}
        loading={addonsLoading || isTabGenerating("summary")}
        error={
          !isTabGenerating("summary") &&
          (summary.error || isTabErrored("summary"))
            ? t("experienceV2.summary.error")
            : undefined
        }
        analyticEvents={summaryAnalyticEvents}
      />,
      addonsLoading || isTabGenerating("summary")
    ),
    quiz: withAddOnEditHeader(
      "quiz",
      <Quiz
        questions={quiz.questions}
        questionsPerAttempt={quizQuestionsPerAttempt}
        loading={addonsLoading || isTabGenerating("quiz")}
        error={!isTabGenerating("quiz") && (quiz.error || isTabErrored("quiz"))}
        analyticEvents={quizAnalyticEvents}
      />,
      addonsLoading || isTabGenerating("quiz"),
      true
    ),
    // bookmarks: ( // MED-6966: not wired on the BE yet, hide everywhere
    //   <AddonPlaceholder label={t("experienceV2.knowledgeHub.tabs.bookmarks")} />
    // ),
    flashcards: withAddOnEditHeader(
      "flashcards",
      <QuickRecall
        cards={quickRecall.cards}
        loading={addonsLoading || isTabGenerating("flashcards")}
        error={
          !isTabGenerating("flashcards") &&
          (quickRecall.error || isTabErrored("flashcards"))
        }
        analyticEvents={quickRecallAnalyticEvents}
      />,
      addonsLoading || isTabGenerating("flashcards"),
      true
    ),
    standouts: withAddOnEditHeader(
      "standouts",
      <Standouts
        analyticEvents={standoutsAnalyticEvents}
        standouts={standouts.standouts}
        loading={addonsLoading || isTabGenerating("standouts")}
        error={
          !isTabGenerating("standouts") &&
          (standouts.error || isTabErrored("standouts"))
        }
        onStandoutClick={documentController.onStandoutSourceClick}
      />,
      addonsLoading || isTabGenerating("standouts")
    ),
  };

  const tabs = buildKnowledgeHubTabs({
    visibleTabs,
    content: tabContent,
    translate: t,
    badgeStatuses: tabBadgeStatuses,
  });

  // The Hub's default tab never fires onTabChange; wait for the final tab list
  // and the org/workspace reads, since this event is only ever sent once.
  const initialTabKey =
    enabledAddOnsLoading || isPublicationLoading || addonsLoading
      ? undefined
      : activeTabKey;
  useEffect(() => {
    if (initialTabKey) trackInitialAddonTabOpen(initialTabKey);
  }, [initialTabKey, trackInitialAddonTabOpen]);

  return (
    <ConfigProvider theme={libertifyThemeV2}>
      <ExperiencePageLayout
        sidebarPosition={sidebarPosition}
        onSlotError={reportSlotError}
        sidebarOverlay={
          video.isOpen && videoUrl ? (
            <ChapteredVideoPlayer
              url={videoUrl}
              poster={videoPoster}
              sections={sections}
              cuepoints={cuepoints}
              onCuepointStart={documentController.onCuepointStart}
              onReady={onPlayerReady}
              autoPlay
              onClose={video.requestClose}
              closeLabel={t("experienceV2.video.close")}
            />
          ) : undefined
        }
        {...video.layoutProps}
        knowledgeHubOpen={hubOpen}
        onKnowledgeHubOpenChange={setHubOpen}
        chatbotOpen={chatbotOpen}
        onChatbotOpenChange={setChatbotOpen}
        header={
          <ExperiencePageHeader
            // Creator mode: the admin sidebar is hidden on this page, so the
            // breadcrumb is the way back to the list. One trail instead of a
            // Back button + title; a long experience name is truncated.
            title={
              <Breadcrumb
                size="md"
                ellipsis
                separator={<CaretRightIcon />}
                items={[
                  {
                    label: t("src.App.projects"),
                    as: Link,
                    to: EXPERIENCES_LIST_PATH,
                  },
                  {
                    label:
                      project?.title ??
                      t("experienceV2.document.title", {
                        id: instanceId ?? "unknown",
                      }),
                  },
                ]}
              />
            }
            // White-labelled orgs get their own mark in place of the
            // Libertify wordmark; everyone else falls through to the default.
            logo={
              isSkemaOrganization(organizationId) ? <SkemaLogo /> : undefined
            }
            logoAs={Link}
            logoTo={HOME_PATH}
            actions={
              // One CTAButton per CTA configured on the project (see
              // ctaButtons above) — Share below is the only fixed action.
              <>
                {ctaButtons.map((cta) => (
                  <CTAButton key={cta.id} icon={cta.icon} onClick={cta.onClick}>
                    {cta.label}
                  </CTAButton>
                ))}
                {/* Separates the project-configured viewer CTAs from the
                  fixed creator actions (Edit / Share). */}
                {ctaButtons.length > 0 && (
                  <Divider orientation="vertical" spacing="none" />
                )}
                <CTAButton
                  icon={<ChartBarIcon size={14} />}
                  onClick={() =>
                    navigate(`/media/projects/${projectId}/analytics`)
                  }
                >
                  {t("experienceV2.document.actions.analytics")}
                </CTAButton>
                {/* Export SCORM opens the SCORM export modal (its submit button
                  does the export) — the NEW-experience entry point, targeting
                  the /experience route. Shown only for admin / whitelisted
                  orgs. */}
                {isScormExportEnabled && (
                  <CTAButton
                    icon={<DownloadSimpleIcon size={14} />}
                    onClick={() => setScormModalOpen(true)}
                  >
                    {t("experienceV2.document.actions.exportScorm")}
                  </CTAButton>
                )}
                {/* Creator-mode entry to the experience edit page. The route
                  itself is wired in a follow-up PR (MED-6946). */}
                <CTAButton
                  icon={<PencilSimpleIcon size={14} />}
                  onClick={() =>
                    navigate(getEditPagePath(projectId, effectiveInstanceId))
                  }
                >
                  {t("experienceV2.document.actions.edit")}
                </CTAButton>
                {!isLti && !isProjectInstanceLoading && (
                  <CTAButton
                    variant="primary"
                    icon={<ShareNetworkIcon size={14} />}
                    onClick={() => setShareModalOpen(true)}
                  >
                    {t("experienceV2.document.actions.share")}
                  </CTAButton>
                )}
                {/* Compact stand-in for the floating widget, once dismissed
                  for this project — same action, no confirmation step. */}
                {isOutOfDate && isOutOfDateDismissed && (
                  <OutOfDatePublicationCompactButton
                    isLoading={isOutOfDateStatusLoading}
                    isItemsOutOfDate={isItemsOutOfDate}
                    isPublicationOutOfDate={isPublicationOutOfDate}
                    onRegenerate={handleRegenerateOutOfDate}
                    onUpdate={handleUpdateOutOfDate}
                  />
                )}
                <LabeledToggle
                  checked={isLive}
                  loading={isMutatingStatus}
                  disabled={!canTogglePublish}
                  onChange={handlePublishToggle}
                  toggleLabel={
                    isLive
                      ? t("experienceV2.document.publish.published")
                      : t("experienceV2.document.publish.draft")
                  }
                  ariaLabel={t("experienceV2.document.publish.ariaLabel")}
                />
              </>
            }
          />
        }
        resizableDocumentSplit={false}
        documentArea={
          <>
            <DocumentSwitcher
              documents={documentTabs}
              activeDocumentId={documentController.activeDocumentId}
              onSelectDocument={documentController.setActiveDocumentId}
            />
            <DocumentPaper>{renderDocument()}</DocumentPaper>
          </>
        }
        knowledgeHub={
          // Hidden entirely for an experience with no add-ons, so the chatbot
          // takes the whole sidebar. Kept mounted (as a spinner) while the
          // enabled read is in flight: an empty list is ambiguous until then,
          // and unmounting on it would reflow the sidebar and reflow back.
          enabledAddOnsLoading || tabs.length > 0 ? (
            <KnowledgeHubPanel
              title={t("experienceV2.knowledgeHub.title")}
              tabs={tabs}
              loading={enabledAddOnsLoading}
              activeKey={activeTabKey}
              onTabChange={(key) => {
                if (activeTabKey && !isTabLoading(activeTabKey))
                  markVisited(activeTabKey);
                setSelectedTabKey(key as TabKey);
                trackAddonTabOpen(key);
              }}
              // Picking an add-on (even the active one) expands the Hub on it
              // and collapses the chat.
              onTabClick={() => {
                setHubOpen(true);
                setChatbotOpen(false);
              }}
            />
          ) : undefined
        }
        chatbot={
          // Chat availability is decided above (`chatOffered`): the project's
          // chat add-on plus its documents, and true while the add-ons are
          // still loading so the skeleton doesn't flash in and out.
          chatOffered ? (
            <ExperienceChatbotPanel
              chat={chatWithEnlarge}
              title={t("experienceV2.chatbot.title")}
              placeholder={t("experienceV2.chatbot.placeholder")}
              sendAriaLabel={t("experienceV2.chatbot.sendAriaLabel")}
              emptyStateTitle={t("experienceV2.chatbot.empty.title")}
              emptyStateSubtitle={t("experienceV2.chatbot.empty.subtitle")}
              onSourceClick={documentController.onSourceClick}
              displayedDocumentId={documentController.activeDocumentId}
              documents={documentSummaries}
            />
          ) : undefined
        }
      />
      {/* Bottom-right toasts: "add-on is ready" (opens its tab) and the
        publish confirmation, sharing one stack so they don't overlap. */}
      <AddOnReadyToasts
        items={toastItems}
        onOpen={openAddOnFromToast}
        onDismiss={dismissAnyToast}
      />
      {/* Floating out-of-date widget — hidden once dismissed, in favor of
        the compact button in the header actions (see `actions` above). */}
      {isOutOfDate && !isOutOfDateDismissed && (
        <OutOfDatePublicationWidget
          isLoading={isOutOfDateStatusLoading}
          isItemsOutOfDate={isItemsOutOfDate}
          isPublicationOutOfDate={isPublicationOutOfDate}
          onRegenerate={handleRegenerateOutOfDate}
          onUpdate={handleUpdateOutOfDate}
          onDismiss={dismissOutOfDateWidget}
          regenerateHint={t("components.RegenerateAction.itemsHint")}
        />
      )}
      {/* TODO(MED-XXXX): interim entry point. The final flow is Share → share
        modal → (if SCORM selected) this modal; tracked in a follow-up PR. */}
      {isScormExportEnabled && (
        <ScormExportModal
          key={scormModuleTitle}
          open={scormModalOpen}
          onClose={() => setScormModalOpen(false)}
          projectId={projectId ?? ""}
          publicationId={effectiveInstanceId}
          experienceTitle={scormModuleTitle}
          playerLaunchUrl={experienceScormUrl}
        />
      )}
      <QuotaUpgradeModal open={showQuotaModal} onClose={dismissQuotaModal} />
      {shareModalOpen && !isLti && (
        <ShareModal
          key={publication?.instance_id ?? effectiveInstanceId}
          open
          onClose={() => setShareModalOpen(false)}
          shareUrl={experienceScormUrl}
          onSave={handleShareSave}
          saving={isSavingShare}
          initialAccess={initialShareAccess}
        />
      )}
    </ConfigProvider>
  );
};

// MED-6966: unused now that bookmarks is hidden — kept commented, not
// removed, since it's the generic placeholder any future add-on can reuse.
// const AddonPlaceholder = ({ label }: { label: string }) => (
//   <div
//     style={{
//       padding: 24,
//       background: "#0F172A",
//       color: "#FFFFFF",
//       borderRadius: 12,
//       textAlign: "center",
//       fontFamily: "sans-serif",
//     }}
//   >
//     {label}
//   </div>
// );

// Centers the loading spinner / error alert inside the DocumentPaper
// while the publication is loading or unavailable.
const centeredStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  minHeight: 240,
};
